/**
 * AbleyLinks legend helpers — wired into `linksConfig.parseLegendLayers`.
 *
 * The AADT layer's `line-color` is a `match` expression on `aadt_band`
 * (see `layer_styles/aadt_style.json`); each band/colour pair becomes a
 * checkable legend item, all enabled by default. The Counts layer is a
 * single-colour circle layer, so it gets one legend item toggling the whole
 * layer.
 *
 * Unlike MIRE, there is no MireDataExplorer overlay, so `syncVisibleLayers`
 * is the only `setFilter` writer and must apply the global
 * `mapDataFilterStore.filter` (e.g. the FRC filter) directly — hence the AADT
 * layer does NOT opt out via `excludeFromMapDataFilter`. The Counts point
 * schema lacks the FRC attribute, so that layer DOES opt out (otherwise the
 * filter would reject every count site).
 */

import { ELinksCore, EAbleyLinksModule, type ILinksSystemLayers } from './interfaces';
import type { MapLayer, MapLegendItem } from '#lib/stores/legendStore.svelte.js';
import { deriveSymbology } from '#lib/components/map/legend/symbology.js';

export function parseLegendLayers(payload: ILinksSystemLayers): MapLayer[] {
	const mapLayers: MapLayer[] = [];

	const aadt = payload?.[EAbleyLinksModule.core]?.[ELinksCore.Aadt];
	if (aadt) {
		// line-color is ['match', ['get', <band attr>], band, colour, …, fallback].
		const lineColor = (aadt.layer.paint as { 'line-color': [string, [string, string]] })[
			'line-color'
		];
		const filterAttr = lineColor[1][1];
		const legendItems: MapLegendItem[] = deriveSymbology({
			type: 'line',
			paint: aadt.layer.paint
		}).map(({ label, swatch }) => ({
			legendItemId: `${aadt.id}:${label.replaceAll(' ', '_')}`,
			filterValue: label,
			label,
			color: swatch.color,
			enabled: true
		}));

		mapLayers.push({
			systemId: aadt.id,
			layerId: aadt.layer.id as string,
			initialFilter: aadt.layer.filter,
			minzoom: aadt.layer.minzoom,
			filterAttr,
			legendItems,
			layerAllEnabled: true,
			layerAnyEnabled: true
		});
	}

	const counts = payload?.[EAbleyLinksModule.core]?.[ELinksCore.Counts];
	if (counts) {
		mapLayers.push({
			systemId: counts.id,
			layerId: counts.layer.id as string,
			initialFilter: counts.layer.filter,
			minzoom: counts.layer.minzoom,
			filterAttr: '',
			legendItems: [
				{
					legendItemId: `${counts.id}:circle`,
					label: 'Count site',
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					color: (counts.layer as any).paint['circle-color'] as string,
					enabled: false
				}
			],
			layerAllEnabled: false,
			layerAnyEnabled: false,
			excludeFromMapDataFilter: true
		});
	}

	return mapLayers;
}
